import { Award, Users, MapPin, Heart, ShieldCheck, Truck, ArrowRight } from 'lucide-react';

export const About = () => {
  return (
    <div className="min-h-screen bg-ivory pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-beige">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <span className="overline mb-4 block">Our Story</span>
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-light text-charcoal max-w-2xl">
            Crafting Excellence<br />Since 2022
          </h1>
          <p className="text-lg text-charcoal-light max-w-xl mt-6">
            Your trusted destination for premium men's fashion, where quality meets affordability.
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="overline mb-4 block">The Beginning</span>
              <h2 className="font-heading text-3xl md:text-5xl font-light text-charcoal mb-8">
                A Vision for<br />Premium Fashion
              </h2>
              <div className="space-y-6 text-charcoal-light leading-relaxed">
                <p>
                  Established in 2022, JAT Fashion House was born from a simple belief: every man deserves 
                  access to premium quality fashion without compromise. In the heart of Kutch, Gujarat, 
                  we set out to redefine men's style.
                </p>
                <p>
                  With two flagship locations in Bhuj and Anjar, we've become the region's most trusted 
                  destination for fashion-forward men who appreciate quality, craftsmanship, and exceptional value.
                </p>
                <p>
                  Our exclusive collections, including the signature ₹999 line, bring international fashion 
                  standards to local wardrobes. Every piece is carefully curated to ensure you always look 
                  and feel your best.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/1884581/pexels-photo-1884581.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="JAT Fashion House Store"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-charcoal">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="font-heading text-5xl md:text-6xl font-light text-gold mb-2">2022</p>
              <p className="text-xs tracking-[0.15em] uppercase text-ivory/60">Established</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-5xl md:text-6xl font-light text-gold mb-2">2</p>
              <p className="text-xs tracking-[0.15em] uppercase text-ivory/60">Flagship Stores</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-5xl md:text-6xl font-light text-gold mb-2">1K+</p>
              <p className="text-xs tracking-[0.15em] uppercase text-ivory/60">Happy Customers</p>
            </div>
            <div className="text-center">
              <p className="font-heading text-5xl md:text-6xl font-light text-gold mb-2">4.8</p>
              <p className="text-xs tracking-[0.15em] uppercase text-ivory/60">Customer Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="overline mb-3 block">Our Promise</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-charcoal">
              Why Choose Us
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-8 bg-beige text-center">
              <Award className="w-8 h-8 mx-auto mb-6 text-gold" strokeWidth={1.5} />
              <h3 className="font-medium text-lg mb-3 text-charcoal">Premium Quality</h3>
              <p className="text-sm text-charcoal-light">High-quality fashion at best prices</p>
            </div>
            <div className="p-8 bg-beige text-center">
              <Heart className="w-8 h-8 mx-auto mb-6 text-gold" strokeWidth={1.5} />
              <h3 className="font-medium text-lg mb-3 text-charcoal">Customer First</h3>
              <p className="text-sm text-charcoal-light">Your satisfaction is our top priority</p>
            </div>
            <div className="p-8 bg-beige text-center">
              <ShieldCheck className="w-8 h-8 mx-auto mb-6 text-gold" strokeWidth={1.5} />
              <h3 className="font-medium text-lg mb-3 text-charcoal">Secure Shopping</h3>
              <p className="text-sm text-charcoal-light">Safe & secure online payments</p>
            </div>
            <div className="p-8 bg-beige text-center">
              <Truck className="w-8 h-8 mx-auto mb-6 text-gold" strokeWidth={1.5} />
              <h3 className="font-medium text-lg mb-3 text-charcoal">Fast Delivery</h3>
              <p className="text-sm text-charcoal-light">Quick shipping across Gujarat and India</p>
            </div>
            <div className="p-8 bg-beige text-center">
              <Users className="w-8 h-8 mx-auto mb-6 text-gold" strokeWidth={1.5} />
              <h3 className="font-medium text-lg mb-3 text-charcoal">Local Presence</h3>
              <p className="text-sm text-charcoal-light">Visit our stores in Bhuj & Anjar</p>
            </div>
            <div className="p-8 bg-beige text-center">
              <MapPin className="w-8 h-8 mx-auto mb-6 text-gold" strokeWidth={1.5} />
              <h3 className="font-medium text-lg mb-3 text-charcoal">WhatsApp Support</h3>
              <p className="text-sm text-charcoal-light">Quick response via WhatsApp</p>
            </div>
          </div>
        </div>
      </section>

      {/* Store Locations */}
      <section className="py-24 md:py-32 bg-beige">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="overline mb-3 block">Visit Us</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-charcoal">
              Our Stores
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-ivory p-10 border border-charcoal/10">
              <MapPin className="w-6 h-6 mb-6 text-gold" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-light text-charcoal mb-2">Bhuj Flagship</h3>
              <p className="text-charcoal-light mb-6">Main Market, Bhuj, Kutch, Gujarat</p>
              <p className="text-sm text-charcoal-light mb-8">Mon - Sun: 10:00 AM - 9:00 PM</p>
              <a 
                href="https://maps.app.goo.gl/bCXXUsFGK4Xawa619" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-charcoal hover:text-gold transition-colors"
              >
                Get Directions
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="bg-ivory p-10 border border-charcoal/10">
              <MapPin className="w-6 h-6 mb-6 text-gold" strokeWidth={1.5} />
              <h3 className="font-heading text-2xl font-light text-charcoal mb-2">Anjar Store</h3>
              <p className="text-charcoal-light mb-6">Station Road, Anjar, Kutch, Gujarat</p>
              <p className="text-sm text-charcoal-light mb-8">Mon - Sun: 10:00 AM - 9:00 PM</p>
              <a 
                href="https://maps.app.goo.gl/bCXXUsFGK4Xawa619" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-charcoal hover:text-gold transition-colors"
              >
                Get Directions
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
